/**
 * React adapter over `TourEngine`.
 *
 * Thin on purpose: it owns state and storage, and delegates everything about
 * running a tour to the engine.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { TaskEngine, IDLE_TASK_STATE, type TaskRunState } from './engine/TaskEngine';
import { TourEngine, type TourResumeToken, type TourRunState } from './engine/TourEngine';
import {
  type TourProgressStore,
  createLocalProgressStore,
  createMemoryProgressStore,
} from './engine/progressStore';
import { validateSandboxLinks } from './sandboxLinks';
import { getTaskForStep, validateTasks } from './tasks';
import type { TaskFlow } from './tasks/types';
import {
  TOURS,
  computeStageProgress,
  getTour,
  validateTours,
  type Tour,
  type TourProgressView,
} from './tours';

export interface TourGuideApi {
  tours: Tour[];
  run: TourRunState;
  /** Completed tours, keyed by tour id. */
  progress: Record<string, boolean>;
  /** Stage pipeline for the live run — empty when idle. */
  stageProgress: TourProgressView;
  start(tourId: string): void;
  stop(): void;
  /** Jump the live run to a step by id. No-op when it is not in this run. */
  goToStep(stepId: string): boolean;
  /** Advance one step; finishes the tour when there is nothing after it. */
  next(): boolean;
  /** Go back one step. No-op on the first. */
  previous(): boolean;

  /* -- Guided creation flows ------------------------------------------- */

  /** The live flow — `running: false` when none. */
  task: TaskRunState;
  /** The flow offered on a tour step, or null. Null too when flows are off. */
  taskFor(stepId: string | null | undefined): { id: string; cta: string } | null;
  /** Pause the tour and start the flow on this step. */
  startTask(stepId: string): boolean;
  /** Advance the flow. Ignored while the active step's goal is unmet. */
  taskNext(): void;
  /**
   * Back one step in the flow — or, when the flow has nothing earlier to go back
   * to, OUT of the flow and back one stop in the tour.
   *
   * That second half is the point. Back was disabled on a flow's first step, so
   * the one control a user reaches for to undo "I opened this by mistake" did
   * nothing at all. Falling out to the previous tour stop is what "back" means
   * there — the flow was entered FROM the tour.
   */
  taskPrevious(): void;
  /** Leave the active step out and carry on to the next one in the flow. */
  taskSkip(): void;
  /**
   * Leave this module's walkthrough entirely and take the tour to the NEXT
   * MODULE — skipping whatever else this module had to show.
   *
   * The whole-module escape, and the one the "Skip" control offers. Skipping a
   * step at a time meant a visitor who did not want to build the thing had to
   * press past every step of it, and then past the rest of the module's
   * submodules too.
   */
  taskSkipModule(): void;
  /**
   * Jump past the current step's whole SEGMENT — see `TaskStep.skipTo`. Only
   * offered on a step that names a later step to land on; a segment that ends
   * the flow is `taskSkipModule`'s job.
   */
  taskSkipSegment(): void;
  /**
   * End the flow and put the interrupted tour back where it was.
   *
   * ONE action for both "I'm done" and "let me out", because the user was in the
   * middle of a tour either way and dropping them onto a page with no tour and no
   * flow would lose the thread entirely.
   *
   * `exit` says where the tour should be left once the resume lands:
   *
   *  - `'stay'` (the default, and `false`) — at the step the flow hung off.
   *    Right for "let me out": they did not finish, so the stop that offered the
   *    flow is still the stop they are on.
   *  - `'step'` (and `true`) — one stop further on. Right for "I'm done": the
   *    user just built the thing that step was describing, so re-showing the
   *    SAME popover reads as the tour not having noticed.
   *  - `'module'` — at the next MODULE, past the rest of this one. The
   *    whole-module skip.
   *  - `'back'` — one stop EARLIER. Backing out of a flow entered by mistake.
   */
  endTask(exit?: TaskExit | boolean): void;
}

/**
 * Where a guided flow leaves the tour when it ends. See `TourGuideApi.endTask`.
 *
 * A NAMED SET rather than a boolean because there are four landings, not two,
 * and the boolean it replaced (`advanceOnResume`) could only express the middle
 * pair. `true`/`false` are still accepted so existing callers keep meaning what
 * they meant.
 */
export type TaskExit = 'stay' | 'step' | 'module' | 'back';

/**
 * How long the tour will wait for a flow's `afterEnd` before carrying on.
 *
 * Cleanup can involve a real navigation (leaving the flow builder routes
 * through the app's unsaved-changes guard, which is a second dialog), so it is
 * not instant — but it must never be able to strand the tour either. Four
 * seconds is comfortably past the slow case and short enough that a hang reads
 * as a pause rather than a freeze.
 */
const AFTER_END_TIMEOUT_MS = 4000;

/**
 * Anything that owns the screen the way a modal does — both roles are used.
 *
 * `#driver-popover-content` is EXCLUDED because driver.js gives its own popover
 * `role="dialog"` (verified in `driver.js/dist/driver.js.mjs`), and the tour is
 * not the mess being cleaned up. It cannot be on screen at this point today —
 * starting a flow destroys the run — but a check that only works because of
 * something two files away is a trap, not a guarantee.
 */
const MODAL_LAYER_SELECTOR = '[role="dialog"]:not(#driver-popover-content), [role="alertdialog"]';

/** The floating drawer's overlay, by the attribute `ui/sheet.tsx` stamps on it. */
const DRAWER_OVERLAY_SELECTOR = '[data-floating-drawer-overlay]';

/** Is any modal layer still on screen? Zero-box elements are not. */
function hasOpenModalLayer(): boolean {
  return Array.from(document.querySelectorAll<HTMLElement>(MODAL_LAYER_SELECTOR)).some((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  });
}

/**
 * Put the PAGE back once the layers are gone — the half no flow can do itself.
 *
 * 🔴 A FLOW'S OWN `afterEnd` CLOSES A LAYER IT CAN STILL SEE. Both of these
 * survive a layer that was never closed at all, and both are invisible to the
 * flow's own predicate:
 *
 *  - An ORPHANED DRAWER OVERLAY. The panel can be unmounted — a route change, a
 *    parent that stopped rendering it — while its overlay stays behind in the
 *    portal. `closeFlowChrome` is gated on the flow's `isOpen()`, which is false
 *    the moment the panel goes, so it never runs; and there is no close control
 *    left to press anyway. What remains is a full-screen `backdrop-blur-[2px]`
 *    over an app that is otherwise working — the reported "the module shows as a
 *    blur", with nothing on screen to explain it.
 *  - `pointer-events: none` ON THE BODY. Radix sets it for the life of a modal
 *    and removes it on close; a modal torn down WITHOUT closing leaves it set,
 *    and the entire page stops responding to the mouse.
 *
 * 🔴 GATED ON THERE BEING NO LAYER LEFT, because both are correct and load-
 * bearing while one is genuinely open — clearing them under a live modal would
 * make the page behind it clickable straight through the overlay.
 *
 * Runs for EVERY flow on EVERY exit, including flows with no `afterEnd` at all:
 * the stuck state does not care which walkthrough was running when it happened.
 */
export function restoreBackground(): void {
  if (hasOpenModalLayer()) return;

  document
    .querySelectorAll<HTMLElement>(DRAWER_OVERLAY_SELECTOR)
    .forEach((overlay) => overlay.remove());

  if (document.body.style.pointerEvents === 'none') {
    document.body.style.removeProperty('pointer-events');
  }
}

/**
 * Run a flow's `afterEnd`, bounded and never rejecting.
 *
 * 🔴 THE TIMEOUT IS A RACE, NOT A CANCEL — the cleanup keeps running, we simply
 * stop waiting on it. Aborting a half-finished cleanup would be worse than a
 * late one: it could leave the dialog it was in the middle of closing.
 */
function settleFlowExit(flow: TaskFlow | null): Promise<void> {
  if (!flow?.afterEnd) return Promise.resolve();

  const cleanup = Promise.resolve()
    .then(() => flow.afterEnd?.())
    .catch((error) => {
      // A cleanup that fails is not a reason to withhold the tour: the user can
      // close whatever is open by hand, and the alternative is no tour at all.
      console.warn(`[tour-guide] task "${flow.id}": afterEnd failed`, error);
    });

  return Promise.race([
    cleanup,
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, AFTER_END_TIMEOUT_MS);
    }),
  ]).then(() => undefined);
}

/**
 * Everything that has to happen between a flow ending and the tour coming back.
 *
 * The flow closes what it opened; this then makes sure the page underneath is
 * actually usable again. Split in that order because `restoreBackground` is
 * deliberately inert while a layer is still up — running it first would do
 * nothing at all.
 */
function settleFlowExitAndBackground(flow: TaskFlow | null): Promise<void> {
  return settleFlowExit(flow).then(() => {
    restoreBackground();
  });
}

const IDLE_PROGRESS: TourProgressView = { stages: [], activeStage: 0, totalStages: 0 };

const Ctx = createContext<TourGuideApi | null>(null);

/**
 * Read the tour API.
 *
 * Returns `null` rather than throwing when there is no provider — the tour is
 * optional chrome, and a component that renders fine without one must not crash
 * a tree that has no provider (tests, `/admin/*`, a disabled build).
 */
export function useTourGuide(): TourGuideApi | null {
  return useContext(Ctx);
}

export interface TourGuideProviderProps {
  children?: React.ReactNode;
  /** Master switch. When false NO context is provided — see below. */
  enabled?: boolean;
  /** Persist completion across reloads. Sandbox-style local storage. */
  persist?: boolean;
  /** Override storage entirely (production API store, tests). */
  progressStore?: TourProgressStore;
  /**
   * Offer the guided creation flows.
   *
   * 🔴 DEFAULTS TO FALSE, and the host must opt in from somewhere that knows it
   * is the sandbox. A flow walks the user through CREATING a real record; in the
   * sandbox that lands in seeded local storage and is thrown away on reload,
   * while in a real workspace it would be a live write to the customer's own
   * account, made by a walkthrough they were only reading.
   */
  tasks?: boolean;
}

export function TourGuideProvider({
  children,
  enabled = true,
  persist = false,
  progressStore,
  tasks = false,
}: TourGuideProviderProps) {
  const store = useMemo<TourProgressStore>(
    () => progressStore ?? (persist ? createLocalProgressStore() : createMemoryProgressStore()),
    [progressStore, persist],
  );

  const [progress, setProgress] = useState<Record<string, boolean>>(() => store.read());
  const [run, setRun] = useState<TourRunState>({
    running: false,
    tourId: null,
    stepId: null,
    index: -1,
    total: 0,
    order: [],
  });

  const [task, setTask] = useState<TaskRunState>(IDLE_TASK_STATE);

  const engineRef = useRef<TourEngine | null>(null);
  const taskEngineRef = useRef<TaskEngine | null>(null);

  /**
   * Where the tour was when a flow took over.
   *
   * A ref, not state: nothing renders from it, and it is written during an event
   * handler that also tears the tour down — a state update there would re-render
   * for no reason and could be read stale by the handler that follows.
   */
  const resumeTokenRef = useRef<TourResumeToken | null>(null);
  /**
   * The flow currently running, kept so `endTask` can hand it its own
   * `afterEnd` — the engine's `stop()` is synchronous and knows nothing about
   * the host page, so putting the app back is the flow's job, not its.
   */
  const activeFlowRef = useRef<TaskFlow | null>(null);

  /**
   * 🔴 REFS, BECAUSE THE ENGINE OUTLIVES EVERY RENDER.
   *
   * The engine is constructed once and keeps the callbacks it was handed for the
   * provider's whole lifetime, so a closure over `tasks` or over `startTask`
   * would freeze whatever those were on the FIRST render — `tasks` would be stuck
   * at its initial value, and `startTask` (declared below) does not even exist
   * yet at construction time. Reading through a ref that each render refreshes is
   * what keeps the engine's view current without rebuilding it.
   */
  const tasksEnabledRef = useRef(tasks);
  tasksEnabledRef.current = tasks;
  const startTaskRef = useRef<(stepId: string) => void>(() => {});

  // One engine for the provider's lifetime. Recreating it per render would drop
  // the high-water index that completion detection depends on.
  if (!engineRef.current) {
    engineRef.current = new TourEngine({
      onState: setRun,
      onComplete: (tourId) => {
        setProgress((prev) => {
          if (prev[tourId]) return prev;
          const next = { ...prev, [tourId]: true };
          store.write(next);
          return next;
        });
      },
      taskOffer: (stepId) => {
        if (!tasksEnabledRef.current) return null;
        const flow = getTaskForStep(stepId);
        return flow ? { label: flow.cta } : null;
      },
      onTaskStart: (stepId) => startTaskRef.current(stepId),
    });
  }

  if (!taskEngineRef.current) {
    taskEngineRef.current = new TaskEngine({
      onState: setTask,
      /**
       * 🔴 A FINISHED FLOW CLOSES WHAT IT OPENED, WITHOUT WAITING TO BE SENT
       * AWAY. `afterEnd` used to run only on `endTask` — the exit ×, Skip
       * module, or "Back to the tour" — so a run that reached its last step sat
       * there with the composer still filling the screen: the completion panel
       * announcing a saved form UNDER a modal that was still up, and the tour's
       * next module then revealed behind it. Reported as the form not closing
       * when the flow is done and the next module opening on top of it.
       *
       * The run is NOT ended here, deliberately: the bar's own "Back to the
       * tour" is what resumes the tour, and taking that decision away would
       * hurry the user past the one panel that says what was built. This closes
       * the composer and puts the page back, nothing else.
       *
       * Safe to run twice — `closeFlowChrome` is idempotent by contract, and
       * the `endTask` that follows calls the same thing again.
       */
      onFinish: () => {
        void settleFlowExitAndBackground(activeFlowRef.current);
      },
    });
  }

  // Surface authoring mistakes in development only — never at runtime for users.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const problems = [...validateTours(), ...validateSandboxLinks(), ...validateTasks()];
    if (problems.length) console.warn('[tour-guide] schema problems:\n - ' + problems.join('\n - '));
  }, []);

  // Tear the overlay down if the provider unmounts mid-tour, so a route change
  // can never leave an orphaned overlay covering the app.
  useEffect(() => () => engineRef.current?.destroy(), []);

  const start = useCallback((tourId: string) => {
    const tour = getTour(tourId);
    if (!tour) {
      console.warn(`[tour-guide] no tour registered with id "${tourId}"`);
      return;
    }
    // Fire-and-forget: `open` prepares the navigation before resolving anything
    // (see `TourEngine.open`), and the launcher has nothing to do with the
    // result — a run that cannot start says so in the console.
    void engineRef.current?.open(tour).then((result) => {
      if (import.meta.env.DEV && result.started && result.skipped.length) {
        // Skipping is correct behaviour (hidden modules, gated features), but it
        // should be observable rather than silent.
        console.info(
          `[tour-guide] "${tour.label}": ${result.resolved.length} steps, ` +
            `skipped ${result.skipped.length} (${result.skipped.join(', ')})`,
        );
      }
    });
  }, []);

  const stop = useCallback(() => {
    // Ending the tour ends any flow it opened. A flow outliving its tour leaves a
    // progress bar and a ring on screen with no tour behind them and no way back —
    // and, because the engine holds `task`, would have blocked the next flow from
    // starting at all.
    taskEngineRef.current?.stop();
    resumeTokenRef.current = null;
    // 🔴 CLEARED, BUT `afterEnd` IS DELIBERATELY NOT RUN. Closing the whole tour
    // is "leave me alone", not "undo what I was doing" — the user may well want
    // to finish the record by hand, and there is no resume here for a leftover
    // modal to sit in front of. Dropping the reference is what stops a later
    // `endTask` from cleaning up after a flow that is no longer running.
    activeFlowRef.current = null;
    engineRef.current?.stop();
  }, []);

  const goToStep = useCallback((stepId: string) => engineRef.current?.goToStep(stepId) ?? false, []);

  const next = useCallback(() => engineRef.current?.next() ?? false, []);

  const previous = useCallback(() => engineRef.current?.previous() ?? false, []);

  /* -- Guided creation flows ------------------------------------------- */

  const taskFor = useCallback((stepId: string | null | undefined) => {
    if (!tasksEnabledRef.current) return null;
    const flow = getTaskForStep(stepId);
    return flow ? { id: flow.id, cta: flow.cta } : null;
  }, []);

  const startTask = useCallback((stepId: string) => {
    if (!tasksEnabledRef.current) return false;
    const flow = getTaskForStep(stepId);
    if (!flow) return false;

    // 🔴 CAPTURE THE TOUR POSITION FIRST. `pauseForTask` destroys the run, and
    // driver.js keeps no state across that — asking afterwards yields nothing to
    // come back to.
    // Pass the flow's own step so the tour comes back THERE, not to whatever the
    // tracker happened to be showing when the user launched it.
    resumeTokenRef.current = engineRef.current?.pauseForTask(stepId) ?? null;
    activeFlowRef.current = flow;
    return taskEngineRef.current?.start(flow) ?? false;
  }, []);

  const taskNext = useCallback(() => taskEngineRef.current?.next(), []);
  const taskSkip = useCallback(() => taskEngineRef.current?.skip(), []);

  const endTask = useCallback((exit: TaskExit | boolean = 'stay') => {
    const landing: TaskExit = exit === true ? 'step' : exit === false ? 'stay' : exit;

    const flow = activeFlowRef.current;
    activeFlowRef.current = null;
    taskEngineRef.current?.stop();
    const token = resumeTokenRef.current;
    resumeTokenRef.current = null;

    /**
     * 🔴 PUT THE APP BACK BEFORE THE TOUR COMES BACK, NOT AFTER.
     *
     * `stop()` above removes the flow's own chrome and nothing else, so a flow
     * left mid-build hands the tour a page with a modal still open on it —
     * and a Radix modal owns the whole screen: `pointer-events: none` on the
     * body, `aria-hidden` on everything outside it, and a dimming backdrop over
     * the lot. Resuming into that draws the tour's highlight UNDERNEATH the
     * backdrop, which is exactly "Skip module does not close the creation page,
     * so the next module is not highlighted and the background is not default".
     *
     * Sequencing it first costs nothing on the common path (`settleFlowExit`
     * resolves immediately for a flow with no `afterEnd`, and for one that has
     * nothing left to undo) and is the whole point on the path that matters.
     */
    const settled = settleFlowExitAndBackground(flow);

    if (!token) return;

    const tour = getTour(token.tourId);
    if (!tour) return;

    // Fire-and-forget: the flow is already closed either way, and a failed
    // resume leaves the tour simply not running rather than breaking anything.
    void settled.then(() => engineRef.current?.resumeFrom(token, tour)).then((landed) => {
      if (!landed) {
        if (import.meta.env.DEV) {
          console.info(
            `[tour-guide] could not resume "${token.tourId}" at step "${token.stepId}" — ` +
              `it is not in the run any more.`,
          );
        }
        return;
      }
      // Only once the resume actually landed — moving past a step the tour never
      // got back to would silently skip it instead of moving past it.
      //
      // 🔴 EVERY MOVE NAMES THE STEP IT IS MEASURED FROM. The resume has just
      // jumped the run, and the engine's own idea of "where we are" comes from
      // driver.js, which lags that jump. Computed from the lagging index instead,
      // "next module" walked on from wherever the run happened to be standing —
      // usually the run's first step, i.e. straight back to the beginning.
      if (landing === 'step') engineRef.current?.next();
      else if (landing === 'module') engineRef.current?.nextModule(token.stepId);
      else if (landing === 'back') engineRef.current?.previousFrom(token.stepId);
    });
  }, []);

  /**
   * Back inside the flow, or out of it when the flow has no earlier step.
   *
   * The engine answers whether it could go back; false is the signal to leave.
   * Deciding that here rather than in the button keeps "what Back means" in one
   * place, and means the callout does not have to know how many steps are behind
   * it (which changes as pass-through steps are walked over).
   */
  const taskPrevious = useCallback(() => {
    if (taskEngineRef.current?.previous()) return;
    endTask('back');
  }, [endTask]);

  /** Leave this module's walkthrough and carry the tour on to the next module. */
  const taskSkipModule = useCallback(() => endTask('module'), [endTask]);

  // Tear a flow down on unmount for the same reason the tour is torn down: its
  // chrome is portaled to <body> and would outlive the tree that owns it.
  useEffect(() => () => taskEngineRef.current?.destroy(), []);

  // Recomputed from the LIVE run, so the tracker follows Next/Back, a jump, and
  // a mid-run close without any separate bookkeeping to keep in sync.
  const stageProgress = useMemo<TourProgressView>(() => {
    if (!run.running || !run.tourId) return IDLE_PROGRESS;
    const tour = getTour(run.tourId);
    if (!tour) return IDLE_PROGRESS;
    return computeStageProgress(tour, run.order, run.stepId);
  }, [run]);

  /**
   * Skip the rest of this segment. The engine handles a jump to a later step; a
   * `skipTo: 'end'` (or a stale target) comes back false and is finished here by
   * ending the flow and advancing the tour — the provider owns the resume token, so
   * only it can do that half.
   */
  const taskSkipSegment = useCallback(() => {
    const handled = taskEngineRef.current?.skipSegment() ?? true;
    // Not handled means the segment had nowhere later to land — so the only
    // honest reading of "past this segment" is "past this module".
    if (!handled) endTask('module');
  }, [endTask]);

  // The engine calls this through `startTaskRef`, which is refreshed here rather
  // than in an effect: the popover button can be clicked in the same commit that
  // rendered it, before effects have run.
  startTaskRef.current = startTask;

  const value = useMemo<TourGuideApi>(
    () => ({
      tours: TOURS,
      run,
      progress,
      stageProgress,
      start,
      stop,
      goToStep,
      next,
      previous,
      task,
      taskFor,
      startTask,
      taskNext,
      taskPrevious,
      taskSkip,
      taskSkipModule,
      taskSkipSegment,
      endTask,
    }),
    [
      run,
      progress,
      stageProgress,
      start,
      stop,
      goToStep,
      next,
      previous,
      task,
      taskFor,
      startTask,
      taskNext,
      taskPrevious,
      taskSkip,
      taskSkipModule,
      taskSkipSegment,
      endTask,
    ],
  );

  // 🔴 A DISABLED MODULE PROVIDES NO CONTEXT AT ALL.
  // Supplying a context whose `start` is a no-op would let the launcher render
  // in a build that cannot run a tour — a visible button that does nothing when
  // clicked, which is worse than no button. `useTourGuide()` returning null is
  // the single signal every consumer already handles. Placed after all hooks so
  // hook order is identical in both branches.
  if (!enabled) return <>{children}</>;

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
